/**
 * Topology Suggestion Hook
 *
 * Builds a spatial index from the site layout (memoized) and
 * suggests topology fields for a given [lng, lat] coordinate.
 */

import { useMemo } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { buildSpatialIndex, suggestTopology } from './suggest';
import type { TopologySuggestion } from '../layout/types';
import type { SiteLayout } from '../layout/types';

// Stable empty fallback
const NO_LAYOUT: SiteLayout | undefined = undefined;

export function useTopologySuggestion(
  lngLat: [number, number] | null,
): TopologySuggestion | null {
  const siteLayout = useProjectStore((s) => s.data.siteLayout) ?? NO_LAYOUT;

  // Memoize the spatial index — only rebuilds when siteLayout changes
  const spatialIndex = useMemo(() => {
    if (!siteLayout?.transform) return null;
    return buildSpatialIndex(siteLayout);
  }, [siteLayout]);

  // Memoize the suggestion — rebuilds when lngLat or index changes
  // No visibleLevels filter: suggestions always consider all topology levels
  // regardless of layer visibility in the Layers panel
  const suggestion = useMemo(() => {
    if (!lngLat || !spatialIndex || !siteLayout) return null;
    return suggestTopology(lngLat[0], lngLat[1], spatialIndex, siteLayout);
  }, [lngLat?.[0], lngLat?.[1], spatialIndex, siteLayout]);

  return suggestion;
}
